import { queryOptions } from '@tanstack/react-query'

import * as hl from '@nktkas/hyperliquid'

const transport = new hl.HttpTransport()
const publicClient = new hl.InfoClient({ transport })

// export type HyperCoreAssets = Awaited<ReturnType<typeof publicClient.spotMeta>>['tokens']
import { SpotToken } from '@nktkas/hyperliquid'
import { TokenSymbol } from '@/domain/types/TokenSymbol'
import { Token } from '@/domain/types/Token'
export type HyperCoreAsset = Awaited<
  SpotToken & {
    systemAddress: string
    universeIndex: number
    midPrice: string
    tradingWith: string[]
    depositContract?: string // For tokens that require special deposit flow (e.g., USDC)
  }
>

export type HyperCoreAssetsType = 'all' | 'withEvmContract'

import { CheckedAddress } from '@/domain/types/CheckedAddress'
import { getAddressBySymbol, TokenSymbolEnum } from '@/utils/tokens'
export const HYPE_TOKEN_ID = '0x0d01dc56dcaaca66ad901c959b4011ec'

// Tokens where HyperCore's evmContract.address is NOT a standard ERC20 (e.g., bridge/deposit contracts)
// We override with the real ERC20 address and store the original as depositContract
const EVM_TOKEN_OVERRIDES: Record<string, { address: string }> = {
  USDC: {
    address: getAddressBySymbol(TokenSymbolEnum.USDC), // Real ERC20 USDC on HyperEVM
  },
}
 
export function hyperCoreAssetsQueryOptions(type: HyperCoreAssetsType = 'withEvmContract') {
  return queryOptions<{ assets: HyperCoreAsset[]; universe: hl.SpotUniverse[] }>({
    // Changed to array type since we're returning an array
    queryKey: hyperCoreAssetsQueryKey(type),
    queryFn: async () => {
      try {
        const [{ tokens, universe }, _sportMetaAndAssetCtxs] = await publicClient.spotMetaAndAssetCtxs()

        const exchangeToInternalNameMap: Map<string, string> = new Map()
        const tokenIndexToSymbolMap: Map<number, string> = new Map()
        const tradingWithMap: Map<string, string[]> = new Map()

        // Single loop to build all maps
        for (const token of tokens) {
          const symbol = token.name.toUpperCase()
          tokenIndexToSymbolMap.set(token.index, symbol)

          tradingWithMap.set(symbol, [])
        }

        // Build exchange map and trading relationships in one loop
        for (const universeItem of universe) {
          const tokenIndex = universeItem.tokens[0]
          const tradingPartnerIndex = universeItem.tokens[1]

          const tokenSymbol = tokenIndex !== undefined ? tokenIndexToSymbolMap.get(tokenIndex) : undefined
          const tradingPartnerSymbol =
            tradingPartnerIndex !== undefined ? tokenIndexToSymbolMap.get(tradingPartnerIndex) : undefined

          if (tokenSymbol) {
            exchangeToInternalNameMap.set(tokenSymbol, universeItem.name)

            // Add trading partner to the map
            if (tradingPartnerSymbol) {
              const currentTradingWith = tradingWithMap.get(tokenSymbol) || []
              tradingWithMap.set(tokenSymbol, [...currentTradingWith, tradingPartnerSymbol])
            }
          }
        }

        return {
          assets: [
            ...tokens
              .map((token: any) => {
                if (token.name.toUpperCase() === 'HYPE') {
                  return {
                    token: new Token({
                      symbol: TokenSymbol('HYPE'),
                      name: 'HYPE',
                      decimals: 18,
                      address: CheckedAddress('0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE'),
                      unitPriceUsd: '0',
                    }),
                    name: 'HYPE',
                    szDecimals: 2,
                    weiDecimals: 8,
                    index: 150,
                    universeIndex: 10000 + 107,
                    tokenId: HYPE_TOKEN_ID,
                    isCanonical: true,
                    midPrice: '0',
                    evmContract: {
                      address: `0x${'2'.repeat(40)}`,
                      evm_extra_wei_decimals: 18,
                    },
                    systemAddress: `0x${'2'.repeat(40)}`,
                    tradingWith: [],
                  }
                }

                const symbol = token.name.toUpperCase()
                const exchangeName = exchangeToInternalNameMap.get(symbol)
                const midPrice = _sportMetaAndAssetCtxs.find((item) => item.coin === exchangeName)?.midPx

                // Check for EVM address overrides (e.g., USDC where evmContract is a bridge, not ERC20)
                const override = EVM_TOKEN_OVERRIDES[symbol]
                const evmContract = override
                  ? {
                      address: override.address,
                      evm_extra_wei_decimals: token.evmContract?.evm_extra_wei_decimals ?? 0,
                    }
                  : token.evmContract
                const depositContract = override ? token.evmContract?.address : undefined

                return {
                  ...token,
                  evmContract,
                  depositContract,
                  midPrice: symbol === 'USDC' ? '1' : midPrice || '0',
                  tradingWith: tradingWithMap.get(symbol) || [],
                  universeIndex: 10000 + (universe.find((t) => t.tokens.join(',') === `${token.index},0`)?.index ?? 0),
                  systemAddress: `0x2${'0'.repeat(39).substr(0, 39 - token.index.toString(16).length)}${token.index.toString(16)}`,
                }
              })
              .filter((token: any) => (type === 'withEvmContract' ? token?.evmContract !== null : true)),
          ],
          universe,
        }
      } catch (error) {
        console.error(error)
        return {
          assets: [],
          universe: [],
        }
      }
    },
  })
}

export function hyperCoreAssetsQueryKey(type: HyperCoreAssetsType): unknown[] {
  return ['hyper-core-assets', type]
}
